import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    InputAdornment,
    Typography,
    Box,
    Tooltip,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import theme from '../../../../theme';

/**
 * Modal for setting deferred cost for deficiencies
 * Allows entering a cost amount per item and shows total cost calculation
 * Provides validation and summary information
 *
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the modal is open
 * @param {Function} props.onClose - Callback when modal is closed
 * @param {Function} props.onSubmit - Callback when cost is submitted with the cost value
 * @param {number} props.selectedCount - Number of items selected
 * @returns {React.ReactElement} Deferred cost modal dialog
 */
const DeferredCostModal = ({ open, onClose, onSubmit, selectedCount }) => {
    // State for the cost input and validation error
    const [cost, setCost] = useState('');
    const [error, setError] = useState('');

    /**
     * Handle cost input changes with validation
     * Only allows valid numeric input with up to 2 decimal places
     *
     * @param {React.ChangeEvent<HTMLInputElement>} e - Change event
     */
    const handleCostChange = (e) => {
        const value = e.target.value;
        if (value === '' || /^\d+\.?\d{0,2}$/.test(value)) {
            setCost(value);
            setError('');
        }
    };

    /**
     * Handle submission of the cost
     * Validates input and calls onSubmit with the numeric cost value
     */
    const handleSubmit = () => {
        if (!cost) {
            setError('Please enter a cost amount');
            return;
        }
        onSubmit(Number(cost));
        onClose();
    };

    // Calculate total cost
    const totalCost = cost ? (Number(cost) * selectedCount).toFixed(2) : '0.00';

    return (
        <Dialog
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: '100%',
                    maxWidth: '400px',
                },
            }}
        >
            <DialogTitle>
                <Typography variant="h6" fontWeight={theme.typography.fontWeights.semiBold}>
                    Set Deferred Cost
                </Typography>
            </DialogTitle>

            <DialogContent>
                {/* Cost input section */}
                <Box sx={{ mb: theme.spacing(2) }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: theme.spacing(1),
                            mb: theme.spacing(1),
                        }}
                    >
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            fontWeight={theme.typography.fontWeights.medium}
                        >
                            Cost Per Item
                        </Typography>
                        <Tooltip
                            title="This cost will be applied to each selected deficiency"
                            arrow
                        >
                            <InfoOutlinedIcon
                                sx={{
                                    fontSize: theme.typography.fontSize.base,
                                    color: theme.palette.grey[500],
                                    cursor: 'help',
                                }}
                            />
                        </Tooltip>
                    </Box>
                    <TextField
                        fullWidth
                        value={cost}
                        type="number"
                        onChange={handleCostChange}
                        error={!!error}
                        helperText={error}
                        placeholder="0.00"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Typography
                                        color="text.secondary"
                                        fontWeight={theme.typography.fontWeights.medium}
                                    >
                                        $
                                    </Typography>
                                </InputAdornment>
                            ),
                            sx: {
                                borderRadius: theme.shape.borderRadius,
                                '& input': {
                                    fontWeight: theme.typography.fontWeights.medium,
                                    fontSize: theme.typography.fontSize.lg,
                                },
                            },
                        }}
                        inputProps={{
                            inputMode: 'decimal',
                            pattern: '\\d*\\.?\\d{0,2}',
                        }}
                    />
                </Box>

                {/* Summary Box */}
                <Box
                    sx={{
                        bgcolor: theme.palette.grey[50],
                        borderRadius: theme.shape.borderRadius,
                        p: theme.spacing(2),
                        mt: theme.spacing(3),
                    }}
                >
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        Summary
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: theme.spacing(1),
                            mt: theme.spacing(1),
                        }}
                    >
                        {/* Cost per item row */}
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                            }}
                        >
                            <Typography variant="body2" color="text.secondary">
                                Cost per item
                            </Typography>
                            <Typography
                                variant="body2"
                                fontWeight={theme.typography.fontWeights.medium}
                            >
                                ${cost ? Number(cost).toFixed(2) : '0.00'}
                            </Typography>
                        </Box>

                        {/* Selected items row */}
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                            }}
                        >
                            <Typography variant="body2" color="text.secondary">
                                Selected items
                            </Typography>
                            <Typography
                                variant="body2"
                                fontWeight={theme.typography.fontWeights.medium}
                            >
                                {selectedCount}
                            </Typography>
                        </Box>

                        {/* Total cost row */}
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                pt: theme.spacing(1),
                                borderTop: `1px solid ${theme.palette.grey[200]}`,
                            }}
                        >
                            <Typography
                                variant="body1"
                                fontWeight={theme.typography.fontWeights.medium}
                            >
                                Total Cost
                            </Typography>
                            <Typography
                                variant="body1"
                                fontWeight={theme.typography.fontWeights.semiBold}
                            >
                                ${totalCost}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} variant="outlined" color="inherit">
                    Cancel
                </Button>
                <Button onClick={handleSubmit} variant="contained" disabled={!cost || !!error}>
                    Set Cost
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DeferredCostModal;
